'use client';

import {
  formError,
  formStart,
  formSubmission,
  formSubmitFailed,
} from '@/lib/analytics';
import { IConsultantFormData } from '@/interfaces';
import { useVisitorCountry } from '@/hooks/useVisitorCountry';
import { isPlausiblePhone, leadFormRules } from '@/lib/form-rules';
import { submitFormToChat } from '@/lib/formSubmission';
import { clearFormSource, getFormSource } from '@/lib/formSource';
import { getThemeButtonClass, getThemeFocusRingClass } from '@/lib/theme';
import { Language } from '@/lib/translations';
import { ITheme } from '@/types/theme';
import { useEffect, useRef, useState } from 'react';
import WaveDivider from './WaveDivider';

interface ConsultantFormProps {
  language: Language;
  title?: string;
  description?: string;
  className?: string;
  businessTypes?: Array<{ id: string; name: string; href: string }>;
  onSuccess?: (formData: IConsultantFormData) => void;
  backgroundColor?: 'primary' | 'secondary';
  theme?: ITheme;
  isContactForm?: boolean;
}

interface FormErrors {
  lineId?: string;
  brandName?: string;
  contactNumber?: string;
  howToCall?: string;
  businessType?: string;
  hearAbout?: string;
  businessStatus?: string;
  interests?: string;
  email?: string;
  message?: string;
}

export default function ConsultantForm({
  language,
  onSuccess,
  backgroundColor = 'primary',
  theme = null,
  isContactForm = false,
}: ConsultantFormProps) {
  const STORAGE_KEY = 'consultantFormData';

  // Get theme-based classes
  const buttonClasses = getThemeButtonClass(theme);
  const focusRingClasses = getThemeFocusRingClass(theme);

  const [formData, setFormData] = useState<IConsultantFormData>({
    brandName: '',
    contactNumber: '',
    howToCall: '',
    businessType: '',
    lineId: '',
    hearAbout: '',
    businessStatus: '',
    interests: [],
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  // Taiwan visitors are contacted on LINE, so their LINE ID is mandatory; elsewhere it is optional.
  const { countryCode } = useVisitorCountry();
  const rules = leadFormRules(countryCode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStartedForm, setHasStartedForm] = useState(false);
  const hasTrackedStart = useRef(false);

  // Load form data from localStorage on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        // Merge so fields added after the data was saved keep their defaults
        setFormData(prev => ({ ...prev, ...parsedData }));
      }
    } catch (error) {
      console.error('Error loading form data from localStorage:', error);
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error('Error saving form data to localStorage:', error);
    }
  }, [formData]);

  // Initialize analytics

  const hearAboutOptions = [
    { en: 'Google Search', zh: 'Google搜尋' },
    { en: 'Social Media', zh: '社群媒體' },
    { en: 'Referral', zh: '朋友推薦' },
    { en: 'Advertisement', zh: '廣告' },
    { en: 'Trade Show', zh: '展覽會' },
    { en: 'Other', zh: '其他' },
  ];

  const businessStatusOptions = [
    { en: 'Planning to open', zh: '計劃開業' },
    { en: 'Recently opened (less than 1 year)', zh: '新開業（不到1年）' },
    { en: 'Established (1-3 years)', zh: '已營業（1-3年）' },
    { en: 'Well-established (3+ years)', zh: '成熟企業（3年以上）' },
  ];

  // Translations
  const t = {
    brandName: language === 'en' ? 'Brand name' : '品牌名稱',
    contactNumber: language === 'en' ? 'Contact number' : '聯絡電話',
    howToCall: language === 'en' ? 'How to call you' : '如何稱呼您',
    businessType: language === 'en' ? 'Your business type' : '您的業務類型',
    lineId: 'LINE ID',
    lineIdRequired:
      language === 'en' ? 'LINE ID is required' : 'LINE ID 是必填的',
    lineIdPlaceholder: language === 'en' ? 'Your LINE ID' : '您的 LINE ID',
    lineIdOptionalPlaceholder:
      language === 'en' ? 'Your LINE ID (optional)' : '您的 LINE ID（選填）',
    contactNumberInvalid:
      language === 'en'
        ? 'Please enter a valid phone number'
        : '請輸入有效的電話號碼',
    hearAbout:
      language === 'en'
        ? 'How did you hear about Omcean Booking?'
        : '您如何得知Omcean Booking?',
    businessStatus: language === 'en' ? 'Business Status' : '業務狀態',
    interests: language === 'en' ? 'What you want to know' : '您想了解什麼',
    email: language === 'en' ? 'Email' : '電子郵件',
    submit: language === 'en' ? 'Submit form' : '提交表格',
    submitting: language === 'en' ? 'Submitting...' : '提交中...',
    success:
      language === 'en' ? 'Form submitted successfully!' : '表格提交成功！',
    brandNameRequired:
      language === 'en' ? 'Brand name is required' : '品牌名稱是必填的',
    contactNumberRequired:
      language === 'en' ? 'Contact number is required' : '聯絡電話是必填的',
    howToCallRequired:
      language === 'en' ? 'How to call you is required' : '如何稱呼您是必填的',
    businessTypeRequired:
      language === 'en' ? 'Business type is required' : '業務類型是必填的',
    hearAboutRequired:
      language === 'en'
        ? 'Please tell us how you heard about us'
        : '請告訴我們您如何得知我們',
    businessStatusRequired:
      language === 'en' ? 'Business status is required' : '業務狀態是必填的',
    interestsRequired:
      language === 'en'
        ? 'Please select at least one interest'
        : '請至少選擇一個感興趣的項目',
    emailRequired:
      language === 'en' ? 'Email address is required' : '電子郵件地址是必填的',
    emailInvalid:
      language === 'en'
        ? 'Please enter a valid email address'
        : '請輸入有效的電子郵件地址',
    selectBusinessType:
      language === 'en' ? 'Select your business type' : '選擇您的業務類型',
    selectOption: language === 'en' ? 'Select an option' : '選擇一個選項',
    selectBusinessStatus:
      language === 'en' ? 'Select your business status' : '選擇您的業務狀態',
  };

  const validateForm = (): { isValid: boolean; errorFields: string[] } => {
    const newErrors: FormErrors = {};

    if (isContactForm) {
      // Simplified validation for contact form
      // Validate name (using brandName field)
      if (!formData.brandName.trim()) {
        newErrors.brandName =
          language === 'en' ? 'Name is required' : '姓名為必填項';
      }

      // Validate email
      if (!formData.email.trim()) {
        newErrors.email = t.emailRequired;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = t.emailInvalid;
      }

      if (!formData.contactNumber.trim()) {
        newErrors.contactNumber = t.contactNumberRequired;
      } else if (!isPlausiblePhone(formData.contactNumber)) {
        newErrors.contactNumber = t.contactNumberInvalid;
      }

      if (rules.lineIdRequired && !formData.lineId.trim()) {
        newErrors.lineId = t.lineIdRequired;
      }

      // Validate message
      if (!formData.message?.trim()) {
        newErrors.message =
          language === 'en' ? 'Message is required' : '訊息為必填項';
      }
    } else {
      // Full consultant form validation
      // Validate brand name
      if (!formData.brandName.trim()) {
        newErrors.brandName = t.brandNameRequired;
      }

      // Validate contact number
      if (!formData.howToCall.trim()) {
        newErrors.howToCall = t.howToCallRequired;
      }

      if (!formData.contactNumber.trim()) {
        newErrors.contactNumber = t.contactNumberRequired;
      } else if (!isPlausiblePhone(formData.contactNumber)) {
        newErrors.contactNumber = t.contactNumberInvalid;
      }

      if (rules.lineIdRequired && !formData.lineId.trim()) {
        newErrors.lineId = t.lineIdRequired;
      }

      // Validate hear about
      if (!formData.hearAbout.trim()) {
        newErrors.hearAbout = t.hearAboutRequired;
      }

      // Validate business status
      if (!formData.businessStatus.trim()) {
        newErrors.businessStatus = t.businessStatusRequired;
      }

      // Validate email
      if (!formData.email.trim()) {
        newErrors.email = t.emailRequired;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = t.emailInvalid;
      }
    }

    setErrors(newErrors);
    const errorFields = Object.keys(newErrors);
    return { isValid: errorFields.length === 0, errorFields };
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // Track form start on first interaction
    if (!hasStartedForm) {
      setHasStartedForm(true);
    }

    // Track analytics form start
    if (!hasTrackedStart.current) {
      hasTrackedStart.current = true;
      const formName = isContactForm ? 'contact_consultant' : 'consultant';
      const formSource = getFormSource();
      formStart(
        formName,
        language,
        formSource ? { form_source: formSource } : undefined
      );
    }

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formName = isContactForm ? 'contact_consultant' : 'consultant';
    const validation = validateForm();
    if (!validation.isValid) {
      formError(formName, validation.errorFields, language);
      return;
    }

    setIsSubmitting(true);

    // Create form message text
    const formType = isContactForm ? 'Contact Form' : 'Consultant Form';
    const messageText = isContactForm
      ? `[${formType}]\nName: ${formData.brandName}\nEmail: ${formData.email}\nPhone: ${formData.contactNumber || 'N/A'}\nLINE ID: ${formData.lineId || 'N/A'}\nMessage: ${formData.message || 'N/A'}`
      : `[${formType}]\nName: ${formData.howToCall}\nBrand: ${formData.brandName}\nBusiness type: ${formData.businessType}\nPhone: ${formData.contactNumber}\nEmail: ${formData.email}\nLINE ID: ${formData.lineId || 'N/A'}\nHow heard: ${formData.hearAbout}\nBusiness status: ${formData.businessStatus}\nInterests: ${formData.interests.join(', ')}`;

    const result = await submitFormToChat({
      language,
      messageText,
      name: isContactForm ? formData.brandName : formData.howToCall,
      email: formData.email,
      formType: isContactForm ? 'contact' : 'consultant',
      formAnswers: {
        name: isContactForm ? formData.brandName : formData.howToCall,
        business_name: formData.brandName,
        email: formData.email,
        phone: formData.contactNumber,
        business_status: formData.businessStatus,
        hear_about: formData.hearAbout,
        business_type: formData.businessType,
        interests: formData.interests,
        line_id: formData.lineId,
        message: formData.message || '',
      },
    });

    if (result.success) {
      // Track successful submission
      const submittedSource = getFormSource();
      formSubmission(formName, language, {
        businessStatus: formData.businessStatus,
        hearAbout: formData.hearAbout,
        businessType: formData.businessType,
        interests: formData.interests,
        ...(submittedSource ? { form_source: submittedSource } : {}),
      });
      clearFormSource();

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(formData);
      }

      // Reset form and clear localStorage
      const emptyFormData = {
        brandName: '',
        contactNumber: '',
        howToCall: '',
        businessType: '',
        lineId: '',
        hearAbout: '',
        businessStatus: '',
        interests: [],
        email: '',
        message: '',
      };
      setFormData(emptyFormData);
      localStorage.removeItem(STORAGE_KEY);

      setHasStartedForm(false);
      hasTrackedStart.current = false;
    } else {
      formSubmitFailed(formName, language, result.error);
      alert(
        language === 'en'
          ? 'Something went wrong. Please try again.'
          : '出現錯誤，請重試。'
      );
    }

    setIsSubmitting(false);
  };

  const defaultTitle = isContactForm
    ? language === 'en'
      ? 'Connect with our team'
      : '聯繫我們的團隊'
    : language === 'en'
      ? 'Connect with our consultant'
      : '聯繫我們的顧問';

  const contactInfo = {
    email: {
      en: 'hello@omceanbooking.com',
      zh: 'hello@omceanbooking.com',
    },
    line: {
      en: '@letsbook',
      zh: '@letsbook',
    },
  };

  const formContent = (
    <div className="grid lg:grid-cols-[500px_1fr] gap-0 rounded-none sm:rounded-3xl overflow-hidden shadow-xl max-w-7xl mx-auto border-0 sm:border border-gray-100 dark:border-gray-800">
      {/* Left Panel - Mailbox Illustration */}
      <div className="relative bg-gray-50 dark:bg-gray-800 p-8 lg:p-12 flex flex-col justify-between items-center overflow-hidden">
        {/* Floating elements in background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-16 h-16 bg-blue-200 rounded-full opacity-20 animate-float"></div>
          <div className="absolute top-40 right-10 w-12 h-12 bg-purple-200 rounded-full opacity-20 animate-float-delayed"></div>
          <div className="absolute bottom-20 right-16 w-20 h-20 bg-pink-200 rounded-full opacity-20 animate-float"></div>
          <div className="absolute top-24 left-32 w-10 h-10 bg-green-200 rounded-full opacity-15 animate-float-delayed"></div>
          <div className="absolute bottom-32 left-8 w-14 h-14 bg-yellow-200 rounded-full opacity-20 animate-float"></div>
          <div className="absolute top-60 right-20 w-8 h-8 bg-indigo-200 rounded-full opacity-15 animate-float-delayed"></div>
          <div className="absolute bottom-40 right-32 w-12 h-12 bg-teal-200 rounded-full opacity-20 animate-float"></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 text-center max-w-md flex-1 flex flex-col justify-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            {defaultTitle}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-8 text-lg">
            {language === 'en'
              ? "Send us a message and we'll respond within 24 hours"
              : '發送消息，我們將在24小時內回覆'}
          </p>

          {/* Mailbox Illustration */}
          <div className="relative mb-8">
            {/* Mailbox */}
            <div className="relative mx-auto w-48 h-48">
              {/* Mailbox body */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-t-3xl shadow-lg">
                {/* Mailbox flag */}
                <div className="absolute -right-2 top-4 w-8 h-1 bg-red-500 rounded-full"></div>
                <div className="absolute -right-4 top-2 w-2 h-6 bg-red-500 rounded-full"></div>
              </div>

              {/* Mailbox door */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-t-2xl border-2 border-blue-400"></div>

              {/* Flying envelope 1 */}
              <div className="absolute top-8 left-4 transform -rotate-12 animate-envelope-float">
                <div className="w-12 h-8 bg-white rounded shadow-md border border-gray-200 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-0.5 bg-blue-400"></div>
                  </div>
                  <div className="absolute top-0 left-0 w-full h-full border-t-8 border-l-8 border-r-8 border-transparent border-t-blue-400 rounded-t"></div>
                </div>
              </div>

              {/* Flying envelope 2 */}
              <div className="absolute top-4 right-6 transform rotate-12 animate-envelope-float-delayed">
                <div className="w-10 h-7 bg-white rounded shadow-md border border-gray-200 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-0.5 bg-purple-400"></div>
                  </div>
                  <div className="absolute top-0 left-0 w-full h-full border-t-8 border-l-8 border-r-8 border-transparent border-t-purple-400 rounded-t"></div>
                </div>
              </div>

              {/* Flying envelope 3 */}
              <div className="absolute top-16 right-2 transform rotate-6 animate-envelope-float">
                <div className="w-11 h-7 bg-white rounded shadow-md border border-gray-200 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-7 h-0.5 bg-pink-400"></div>
                  </div>
                  <div className="absolute top-0 left-0 w-full h-full border-t-8 border-l-8 border-r-8 border-transparent border-t-pink-400 rounded-t"></div>
                </div>
              </div>

              {/* Sparkles */}
              <div className="absolute top-12 left-8 text-yellow-400 text-2xl animate-sparkle">
                ✨
              </div>
              <div className="absolute top-20 right-12 text-yellow-400 text-xl animate-sparkle-delayed">
                ⭐
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="relative z-10 space-y-2 text-left w-full max-w-md">
          <a
            href={`mailto:${contactInfo.email[language]}`}
            className="flex items-center space-x-3 hover:opacity-70 transition-opacity"
          >
            <svg
              className="w-5 h-5 text-gray-700 dark:text-gray-300 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <p className="text-xs text-gray-700 dark:text-gray-300">
              {contactInfo.email[language]}
            </p>
          </a>
          <a
            href="https://lin.ee/FevzfW6"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 hover:opacity-70 transition-opacity"
          >
            <svg
              className="w-5 h-5 text-gray-700 dark:text-gray-300 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"></path>
            </svg>
            <p className="text-xs text-gray-700 dark:text-gray-300">
              {contactInfo.line[language]}
            </p>
          </a>
        </div>

        {/* Custom CSS for animations */}
        <style jsx>{`
          @keyframes float {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }
          @keyframes float-delayed {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-15px);
            }
          }
          @keyframes envelope-float {
            0%,
            100% {
              transform: translateY(0px) rotate(-12deg);
            }
            50% {
              transform: translateY(-10px) rotate(-12deg);
            }
          }
          @keyframes envelope-float-delayed {
            0%,
            100% {
              transform: translateY(0px) rotate(12deg);
            }
            50% {
              transform: translateY(-15px) rotate(12deg);
            }
          }
          @keyframes sparkle {
            0%,
            100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              transform: scale(1.2);
            }
          }
          @keyframes sparkle-delayed {
            0%,
            100% {
              opacity: 0.7;
              transform: scale(1);
            }
            50% {
              opacity: 1;
              transform: scale(1.3);
            }
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          .animate-float-delayed {
            animation: float-delayed 4s ease-in-out infinite;
          }
          .animate-envelope-float {
            animation: envelope-float 2s ease-in-out infinite;
          }
          .animate-envelope-float-delayed {
            animation: envelope-float-delayed 2.5s ease-in-out infinite;
          }
          .animate-sparkle {
            animation: sparkle 1.5s ease-in-out infinite;
          }
          .animate-sparkle-delayed {
            animation: sparkle-delayed 2s ease-in-out infinite;
          }
        `}</style>
      </div>

      {/* Right Panel - Form */}
      <div className="bg-white dark:bg-gray-900 p-8 lg:p-12">
        <form onSubmit={handleSubmit} className="space-y-5">
          {isContactForm ? (
            <>
              {/* Simplified Contact Form */}
              {/* Name Field */}
              <div>
                <label
                  htmlFor="brandName"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {language === 'en' ? 'Name' : '姓名'}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="brandName"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.brandName
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={language === 'en' ? 'Your name' : '您的姓名'}
                />
                {errors.brandName && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.brandName}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {language === 'en' ? 'Email' : '電子郵件'}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.email
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    language === 'en' ? 'your@email.com' : '您的電子郵件'
                  }
                />
                {errors.email && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label
                  htmlFor="contactNumber"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {language === 'en' ? 'Phone' : '電話'}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.contactNumber
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    language === 'en' ? 'Your phone number' : '您的電話號碼'
                  }
                />
                {errors.contactNumber && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.contactNumber}
                  </p>
                )}
              </div>

              {/* LINE ID — required for Taiwan visitors, optional elsewhere */}
              <div>
                <label
                  htmlFor="lineId"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.lineId}
                  {rules.lineIdRequired && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>
                <input
                  type="text"
                  id="lineId"
                  name="lineId"
                  value={formData.lineId}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.lineId
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    rules.lineIdRequired
                      ? t.lineIdPlaceholder
                      : t.lineIdOptionalPlaceholder
                  }
                />
                {errors.lineId && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.lineId}
                  </p>
                )}
              </div>

              {/* Message Field */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {language === 'en' ? 'Message' : '訊息'}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message || ''}
                  onChange={handleInputChange}
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors resize-none`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.message
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    language === 'en'
                      ? 'How can we help you?'
                      : '我們可以如何幫助您？'
                  }
                />
                {errors.message && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.message}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Full Consultant Form */}
              {/* Brand Name - Required */}
              <div>
                <label
                  htmlFor="brandName"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.brandName} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="brandName"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.brandName
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    language === 'en'
                      ? 'Enter your brand name'
                      : '輸入您的品牌名稱'
                  }
                />
                {errors.brandName && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.brandName}
                  </p>
                )}
              </div>

              {/* How to Call - Required */}
              <div>
                <label
                  htmlFor="howToCall"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.howToCall} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="howToCall"
                  name="howToCall"
                  value={formData.howToCall}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.howToCall
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    language === 'en' ? 'Enter your name' : '輸入您的稱呼'
                  }
                />
                {errors.howToCall && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.howToCall}
                  </p>
                )}
              </div>

              {/* LINE ID - Required */}
              <div>
                <label
                  htmlFor="lineId"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.lineId}
                  {rules.lineIdRequired && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>
                <input
                  type="text"
                  id="lineId"
                  name="lineId"
                  value={formData.lineId}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.lineId
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    rules.lineIdRequired
                      ? t.lineIdPlaceholder
                      : t.lineIdOptionalPlaceholder
                  }
                />
                {errors.lineId && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.lineId}
                  </p>
                )}
              </div>

              {/* Contact Number (Optional) */}
              <div>
                <label
                  htmlFor="contactNumber"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.contactNumber} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.contactNumber
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    language === 'en'
                      ? 'Enter your phone number'
                      : '輸入您的電話號碼'
                  }
                />
                {errors.contactNumber && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.contactNumber}
                  </p>
                )}
              </div>

              {/* Email (Optional) */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.email} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.email
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder={
                    language === 'en'
                      ? 'Enter your email address'
                      : '輸入您的電子郵件地址'
                  }
                />
                {errors.email && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.email}
                  </p>
                )}
              </div>

              {/* How did you hear about us (Optional) */}
              <div>
                <label
                  htmlFor="hearAbout"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.hearAbout} <span className="text-red-500">*</span>
                </label>
                <select
                  id="hearAbout"
                  name="hearAbout"
                  value={formData.hearAbout}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 pr-12 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors appearance-none bg-no-repeat bg-right`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.hearAbout
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: formData.hearAbout
                      ? 'var(--color-input-text)'
                      : 'var(--color-input-placeholder)',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 1rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  <option value="">{t.selectOption}</option>
                  {hearAboutOptions.map((option, index) => (
                    <option key={index} value={option[language]}>
                      {option[language]}
                    </option>
                  ))}
                </select>
                {errors.hearAbout && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.hearAbout}
                  </p>
                )}
              </div>

              {/* Business Status (Optional) */}
              <div>
                <label
                  htmlFor="businessStatus"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-foreground)' }}
                >
                  {t.businessStatus} <span className="text-red-500">*</span>
                </label>
                <select
                  id="businessStatus"
                  name="businessStatus"
                  value={formData.businessStatus}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 pr-12 border rounded-lg outline-none focus:ring-2 ${focusRingClasses} focus:border-transparent transition-colors appearance-none bg-no-repeat bg-right`}
                  style={{
                    backgroundColor: 'var(--color-input-background)',
                    borderColor: errors.businessStatus
                      ? 'var(--color-destructive)'
                      : 'var(--color-input-border)',
                    color: formData.businessStatus
                      ? 'var(--color-input-text)'
                      : 'var(--color-input-placeholder)',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 1rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  <option value="">{t.selectBusinessStatus}</option>
                  {businessStatusOptions.map((status, index) => (
                    <option key={index} value={status[language]}>
                      {status[language]}
                    </option>
                  ))}
                </select>
                {errors.businessStatus && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: 'var(--color-destructive)' }}
                  >
                    {errors.businessStatus}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full ${buttonClasses} text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? t.submitting : t.submit}
            </button>
          </div>
        </form>

        <style jsx>{`
          /* Custom placeholder styling for theme support */
          input::placeholder {
            color: var(--color-input-placeholder) !important;
            opacity: 1;
          }

          input::-webkit-input-placeholder {
            color: var(--color-input-placeholder) !important;
          }

          input::-moz-placeholder {
            color: var(--color-input-placeholder) !important;
          }

          input:-ms-input-placeholder {
            color: var(--color-input-placeholder) !important;
          }

          input:-moz-placeholder {
            color: var(--color-input-placeholder) !important;
          }

          /* Style select options for better contrast */
          select option {
            background-color: var(--color-input-background);
            color: var(--color-input-text);
          }

          /* Custom select caret styling for dark mode */
          .dark select {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
          }
        `}</style>
      </div>
    </div>
  );

  const bgClasses =
    backgroundColor === 'primary'
      ? 'bg-white dark:bg-gray-900'
      : 'bg-gradient-to-b from-blue-50/30 to-blue-50/50 dark:from-gray-800 dark:to-gray-900';

  return (
    <section id="contact-form" className={`${bgClasses} py-20 relative`}>
      {/* Wave at top */}
      <WaveDivider
        position="top"
        backgroundColor={backgroundColor}
        theme={theme}
      />
      <div
        className="w-full max-w-[1400px] mx-auto px-0 sm:px-6 lg:px-8"
        style={{ marginTop: backgroundColor === 'secondary' ? '50px' : '0' }}
      >
        <div>{formContent}</div>
      </div>
      <WaveDivider
        position="bottom"
        backgroundColor={backgroundColor}
        theme={theme}
      />
    </section>
  );
}
